import { z } from "zod";

import { fetchWithError, useParsedQueries, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { lineStringSchema } from "../geojson";
import { paginatedSchema } from "../pagination";

import { baseAssetWithTimeseriesSchema } from "./assetsBase";

export const leveeListItemSchema = baseAssetWithTimeseriesSchema.extend({
  geometry: lineStringSchema,
  category: z.string().nullable(),
  levee_type: z.string().nullable(),
  name: z.string().nullable(),
  recurrence_time: z.number().nullable(),
  material: z.string().nullable(),
  coating: z.string().nullable(),
  crest_height: z.number().nullable(),
});

export type LeveeListItem = z.infer<typeof leveeListItemSchema>;

export const leveeListSchema = paginatedSchema(leveeListItemSchema);
export type LeveeList = z.infer<typeof leveeListSchema>;

export const leveeSchema = leveeListItemSchema;
export type Levee = z.infer<typeof leveeSchema>;

export function useLevees(params = {}) {
  const url = combineUrlAndParams("/api/v4/levees/", params);
  return useParsedQuery<LeveeList>(
    ["lizard-api-v4", "levees", url],
    () => fetchWithError(url),
    (json: unknown) => leveeListSchema.parse(json)
  );
}

export function useLevee(id: number) {
  return useParsedQuery<Levee>(
    ["lizard-api-v4", "levees", id],
    () => fetchWithError(`/api/v4/levees/${id}/`),
    (json: unknown) => leveeSchema.parse(json)
  );
}

export function useMultipleLevees(ids: number[]) {
  return useParsedQueries(
    ids.map((id) => {
      return {
        queryKey: ["lizard-api-v4", "levees", id],
        fetchFunction: () => fetchWithError(`/api/v4/levees/${id}/`),
        parseFunction: (json: unknown) => leveeSchema.parse(json),
      };
    })
  );
}
